// V·IAnet Governance — AdminOverlayPanel (prompt 0483)
// Overlay no-bloqueante para paneles admin de detalle.
// Permite click-to-switch: clics que caen en el backdrop pasan a través
// a la tabla debajo (pointer-events: none en el backdrop, auto en el panel).

import { useEffect } from 'react';

export interface AdminOverlayPanelProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  children: React.ReactNode;
  maxWidth?: number; // default 980
  heightVh?: number; // default 88
  showCloseButton?: boolean; // default true
}

function CloseIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}

export function AdminOverlayPanel({
  open,
  onClose,
  title,
  children,
  maxWidth = 980,
  heightVh = 88,
  showCloseButton = true,
}: AdminOverlayPanelProps) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.5)',
        zIndex: 200,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
        pointerEvents: 'none', // clave: clics en el backdrop pasan a la tabla
      }}
    >
      <div
        style={{
          position: 'relative',
          width: '100%',
          maxWidth,
          height: `${heightVh}vh`,
          borderRadius: 12,
          background: '#fff',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
          pointerEvents: 'auto', // el panel sí es interactivo
        }}
      >
        {title && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '16px 24px',
              borderBottom: '1px solid #e5e7eb',
              fontFamily: 'DM Sans, sans-serif',
              fontWeight: 600,
              fontSize: 16,
              flexShrink: 0,
            }}
          >
            <span>{title}</span>
            {showCloseButton && (
              <button
                onClick={onClose}
                aria-label="Cerrar"
                style={{
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  padding: 4,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#5A6171',
                }}
              >
                <CloseIcon />
              </button>
            )}
          </div>
        )}
        {!title && showCloseButton && (
          <button
            onClick={onClose}
            aria-label="Cerrar"
            style={{
              position: 'absolute',
              top: 12,
              right: 12,
              zIndex: 10,
              background: 'rgba(255,255,255,0.9)',
              border: '1px solid #E2E6ED',
              borderRadius: 6,
              cursor: 'pointer',
              padding: 6,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#5A6171',
            }}
          >
            <CloseIcon />
          </button>
        )}
        {children}
      </div>
    </div>
  );
}
